import { baseApi } from "./baseApi";
import {
  CreateQuestionRequest,
  UpdateQuestionRequest,
  QuestionImageUploadResponse,
  UpdateQuestionCorrectOptionRequest,
} from "../../interfaces";
import { QuestionModelEnum } from "../../enums";

export const questionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getQuestion: builder.query<any, string>({
      query: (id) => ({ url: `/api/question/${id}` }),
      providesTags: ["Question"],
    }),
    getQuestionsByLesson: builder.query<
      any,
      { lessonId: string; model?: QuestionModelEnum }
    >({
      query: ({ lessonId, model }) =>
        model
          ? { url: `/api/question/${lessonId}/lesson?model=${model}` }
          : { url: `/api/question/${lessonId}/lesson` },
      providesTags: ["Questions-By-Lesson"],
    }),
    addQuestion: builder.mutation<any, CreateQuestionRequest>({
      query: (data) => ({
        url: "/api/question",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Questions-By-Lesson"],
    }),
    updateQuestion: builder.mutation<
      any,
      { id: string; data: UpdateQuestionRequest }
    >({
      query: ({ id, data }) => ({
        url: `/api/question/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Question", "Questions-By-Lesson"],
    }),
    deleteQuestion: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/question/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Questions-By-Lesson"],
    }),
    toggleQuestionState: builder.mutation<any, string>({
      query: (id) => ({
        url: `/api/question/toggle-state/${id}`,
        method: "PATCH",
      }),
      invalidatesTags: ["Question", "Questions-By-Lesson"],
    }),
    uploadQuestionImage: builder.mutation<
      QuestionImageUploadResponse,
      {
        file: File;
        tempId: string;
        onProgress?: (progress: number, tempId: string) => void;
      }
    >({
      query: ({ file, tempId, onProgress }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/api/question/upload/image/${tempId}`,
          method: "POST",
          data: formData,
          onUploadProgress: (event: any) => {
            if (event.total && onProgress) {
              const progress = Math.round((event.loaded / event.total) * 100);
              onProgress(progress, tempId);
            }
          },
        };
      },
      invalidatesTags: ["Question", "Questions-By-Lesson"],
    }),
    updateQuestionCorrectOption: builder.mutation<
      any,
      UpdateQuestionCorrectOptionRequest
    >({
      query: (data) => ({
        url: `/api/question/answer/correct-option/${data.id}`,
        method: "PATCH",
        body: { correctOption: data.correctOption },
      }),
      invalidatesTags: ["Question", "Questions-By-Lesson"],
    }),
  }),
});

export const {
  useGetQuestionQuery,
  useGetQuestionsByLessonQuery,
  useAddQuestionMutation,
  useUpdateQuestionMutation,
  useDeleteQuestionMutation,
  useToggleQuestionStateMutation,
  useUploadQuestionImageMutation,
  useUpdateQuestionCorrectOptionMutation,
} = questionApi;
